"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  submitFeedback,
  type FeedbackData,
} from "@/lib/feedback/submitFeedback";
import { useAuth } from "@/hooks/useAuth";


const createSchema = (isAuthenticated: boolean) => z.object({
  feedback_text: z.string()
    .min(5, 'Feedback must be at least 5 characters')
    .max(5000, 'Feedback must be less than 5000 characters'),
  email: isAuthenticated 
    ? z.union([z.email('Invalid email format'), z.literal('')]).optional()
    : z.email('Email is required for anonymous feedback'),
  category: z.enum(['general', 'bug', 'feature', 'improvement', 'complaint'])
});


type FormData = z.infer<typeof createSchema extends (arg: any) => any ? ReturnType<typeof createSchema> : never>;

export default function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting, isValid }
  } = useForm<FormData>({
    resolver: zodResolver(createSchema(!!user)),
    defaultValues: {
      feedback_text: '',
      email: user?.email || '',
      category: 'general'
    },
    mode: 'onChange'
  });

  useEffect(() => {
    if (user?.email) {
      setValue('email', user.email);
    }
  }, [user?.email, setValue]);

  const onSubmit = async (data: FormData) => {
    setSubmitError("");

    try {
      const feedbackData: FeedbackData = {
        feedback_text: data.feedback_text.trim(),
        email: data?.email?.trim() || user?.email || undefined,
        category: data.category,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      };

      const result = await submitFeedback(feedbackData);

      if (result.success) {
        setIsSubmitted(true);

        setTimeout(() => {
          resetForm();
        }, 3000);
      } else {
        setSubmitError(result.error || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitError("An unexpected error occurred");
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setIsOpen(false);
    setSubmitError("");
    reset();
  };

  const feedbackText = watch('feedback_text');
  const characterCount = feedbackText?.length || 0;

  return (
    <>
      {/* Feedback Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="w-12 fixed bottom-4 right-4 bg-gradient-to-r from-brand-teal to-brand-amber text-white p-3 rounded-full shadow-lg hover:scale-110 transition-transform z-30"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        💬
      </motion.button>

      {/* Feedback Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-md w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isSubmitted ? (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold text-white">Feedback</h3>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-gray-400 hover:text-white text-xl"
                    >
                      ✕
                    </button>
                  </div>

                  {submitError && (
                    <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 mb-4">
                      <p className="text-red-300 text-sm">{submitError}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* Category Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        {...register('category')}
                        className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-brand-teal"
                      >
                        <option value="general" className="bg-gray-800">
                          General Feedback
                        </option>
                        <option value="bug" className="bg-gray-800">
                          Bug Report
                        </option>
                        <option value="feature" className="bg-gray-800">
                          Feature Request
                        </option>
                        <option value="improvement" className="bg-gray-800">
                          Improvement
                        </option>
                        <option value="complaint" className="bg-gray-800">
                          Complaint
                        </option>
                      </select>
                      {errors.category && (
                        <p className="text-red-300 text-xs mt-1">
                          {errors.category.message}
                        </p>
                      )}
                    </div>

                    {/* Feedback Text Field */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-300">
                          How can we improve Probabl?
                        </label>
                        <span className={`text-xs ${
                          characterCount > 4500 ? 'text-orange-400' : 'text-gray-400'
                        }`}>
                          {characterCount}/5000
                        </span>
                      </div>
                      <textarea
                        {...register('feedback_text')}
                        className={`w-full p-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none ${
                          errors.feedback_text
                            ? 'border-red-500/50 focus:border-red-500' 
                            : 'border-white/20 focus:border-brand-teal'
                        }`}
                        rows={4}
                        placeholder="Share your thoughts, suggestions, or report issues..."
                        maxLength={5000}
                      />
                      {errors.feedback_text && (
                        <p className="text-red-300 text-xs mt-1">
                          {errors.feedback_text.message}
                        </p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email {!user && "(required for anonymous feedback)"}
                      </label>
                      <input
                        type="email"
                        {...register('email')}
                        className={`w-full p-3 bg-white/10 border rounded-xl text-white placeholder-gray-400 focus:outline-none ${
                          errors.email
                            ? 'border-red-500/50 focus:border-red-500' 
                            : 'border-white/20 focus:border-brand-teal'
                        }`}
                        placeholder="your@email.com"
                      />
                      {errors.email && (
                        <p className="text-red-300 text-xs mt-1">
                          {errors.email.message}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {user
                          ? "We'll only use this to follow up on your feedback"
                          : "Required for anonymous feedback so we can respond to you"}
                      </p>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting || !isValid}
                      className="w-full bg-gradient-to-r from-brand-teal to-brand-amber text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100"
                    >
                      {isSubmitting ? "Sending..." : "Send Feedback"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">🙏</div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Thank you!
                  </h3>
                  <p className="text-gray-300">
                    Your feedback helps us make Probabl better for everyone.
                  </p>
                  {user && (
                    <p className="text-sm text-gray-400 mt-2">
                      We'll follow up if needed at the email in your profile.
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}